/**
 * Dashboard Page
 * 
 * Main dashboard showing overview of bot status, recent activity,
 * and quick stats.
 * 
 * Layout Structure:
 * - Top Row: System status + core bot stats (channels, messages, commands)
 * - Middle Row: Module widgets (enabled modules show their status here)
 * - Bottom Row: Recent activity feed (full width)
 */

import { useEffect, useState, useCallback } from 'react'
import { useWebSocket, type WSMessage } from '@/hooks/useWebSocket'
import api from '@/api/client'
import {
  Radio,
  MessageSquare,
  Zap,
  Server,
  Wifi,
  WifiOff,
  Puzzle,
} from 'lucide-react'
import clsx from 'clsx'

// Import module registry for widgets
import { getAllModules } from '@/modules'

// =============================================================================
// Stat Card Component
// =============================================================================

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  color = 'purple',
}: {
  icon: React.ElementType
  label: string
  value: string | number
  subValue?: string
  color?: 'purple' | 'green' | 'blue' | 'orange' | 'gray'
}) {
  const colors = {
    purple: 'bg-synth-magenta/10 text-synth-magenta',
    green: 'bg-state-success/10 text-synth-mint',
    blue: 'bg-state-info/10 text-state-info',
    orange: 'bg-state-warning/10 text-state-warning',
    gray: 'bg-neutral-600/10 text-text-secondary',
  }
  
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-text-secondary mb-1">{label}</p>
          <p className="text-2xl font-bold text-text-primary">{value}</p>
          {subValue && (
            <p className="text-xs text-text-muted mt-1">{subValue}</p>
          )}
        </div>
        <div className={clsx('p-3 rounded-lg', colors[color])}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// System Status Component
// =============================================================================

function SystemStatusCard({
  status,
  isWsConnected,
}: {
  status: {
    status: string
    twitch_bot: string
    websocket_connections: number
  } | null
  isWsConnected: boolean
}) {
  const indicators = [
    {
      label: 'API',
      status: status?.status || 'unknown',
    },
    {
      label: 'Bot',
      status: status?.twitch_bot || 'unknown',
    },
    {
      label: 'WebSocket',
      status: isWsConnected ? 'connected' : 'disconnected',
    },
  ]
  
  const statusColors: Record<string, string> = {
    healthy: 'bg-synth-mint',
    connected: 'bg-synth-mint',
    disconnected: 'bg-state-danger',
    error: 'bg-state-danger',
    unknown: 'bg-neutral-600',
  }
  
  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm text-text-secondary mb-1">System Status</p>
          <p className="text-2xl font-bold text-text-primary">
            {status?.status === 'healthy' ? 'Online' : 'Degraded'}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-state-info/10 text-state-info">
          <Server size={24} />
        </div>
      </div>
      
      <div className="space-y-2">
        {indicators.map(({ label, status: s }) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">{label}</span>
            <div className="flex items-center gap-2">
              <div className={clsx('w-2 h-2 rounded-full', statusColors[s] || statusColors.unknown)} />
              <span className="text-text-secondary capitalize">{s}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// Module Widgets Row
// =============================================================================

interface EnabledModule {
  name: string
  display_name: string
  has_access: boolean
}

function ModuleWidgetsRow({
  enabledModules,
  isConnected,
  selectedChannelId,
}: {
  enabledModules: EnabledModule[]
  isConnected: boolean
  selectedChannelId?: number
}) {
  // Get frontend module definitions
  const frontendModules = getAllModules()
  
  // Filter to modules that have widgets and are enabled
  const modulesWithWidgets = enabledModules.filter((em) => {
    const fm = frontendModules.find((f) => f.name === em.name)
    return fm?.DashboardWidget && em.has_access
  })
  
  if (modulesWithWidgets.length === 0) {
    // Show placeholder when no modules enabled
    return (
      <div className="card bg-surface-raised/50 border-dashed border-border">
        <div className="flex items-center justify-center py-8 text-text-muted">
          <div className="text-center">
            <Puzzle className="mx-auto mb-2 opacity-50" size={32} />
            <p className="text-sm">
              Module widgets will appear here when modules are enabled.
            </p>
            <p className="text-xs text-text-muted mt-1">
              Go to Modules → Enable a module to see its dashboard widget.
            </p>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {modulesWithWidgets.map((em) => {
        const fm = frontendModules.find((f) => f.name === em.name)
        if (!fm?.DashboardWidget) return null
        
        const Widget = fm.DashboardWidget
        return (
          <Widget
            key={em.name}
            channelId={selectedChannelId}
            isConnected={isConnected}
          />
        )
      })}
    </div>
  )
}

// =============================================================================
// Activity Feed Components
// =============================================================================

function ActivityItem({
  type,
  message,
  timestamp,
}: {
  type: string
  message: string
  timestamp: string
}) {
  const typeStyles: Record<string, string> = {
    'chat.command': 'border-synth-magenta',
    'song.now_playing': 'border-synth-mint',
    'bot.status': 'border-state-info',
    default: 'border-border-strong',
  }
  
  const typeLabels: Record<string, string> = {
    'chat.command': 'Command',
    'song.now_playing': 'Now Playing',
    'bot.status': 'Bot Status',
    'chat.message': 'Chat',
    'pong': 'Heartbeat',
  }
  
  return (
    <div
      className={clsx(
        'border-l-2 pl-4 py-2',
        typeStyles[type] || typeStyles.default
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-muted uppercase tracking-wide">
          {typeLabels[type] || type}
        </span>
        <span className="text-xs text-text-muted">{timestamp}</span>
      </div>
      <p className="text-sm text-text-secondary mt-1">{message}</p>
    </div>
  )
}

/**
 * Format activity message based on event type.
 * Defensive against undefined payload/properties.
 */
function formatActivityMessage(msg: WSMessage): string {
  const payload = msg.payload || {}
  
  switch (msg.type) {
    case 'chat.command':
      return `!${payload.command || '?'} triggered by ${payload.user || 'unknown'}`
    case 'song.now_playing':
      return `Now playing: ${payload.title || 'Unknown'} - ${payload.artist || 'Unknown'}`
    case 'bot.status':
      return `Bot ${payload.status || '?'} in #${payload.channel || '?'}`
    case 'pong':
      return 'Server heartbeat'
    default:
      try {
        const str = JSON.stringify(payload)
        return str.length > 100 ? str.slice(0, 100) + '...' : str
      } catch {
        return `[${msg.type}]`
      }
  }
}

// =============================================================================
// Main Dashboard Component
// =============================================================================

export default function Dashboard() {
  const [stats, setStats] = useState({
    activeChannels: 0,
    messagesToday: 0,
    commandsToday: 0,
  })
  const [activity, setActivity] = useState<Array<{
    id: string
    type: string
    message: string
    timestamp: string
  }>>([])
  const [systemStatus, setSystemStatus] = useState<{
    status: string
    twitch_bot: string
    websocket_connections: number
  } | null>(null)
  const [enabledModules, setEnabledModules] = useState<EnabledModule[]>([])
  const [selectedChannelId, setSelectedChannelId] = useState<number | undefined>()
  
  // Get WebSocket connection (shared via context)
  const { isConnected, addMessageHandler } = useWebSocket()
  
  /**
   * Handle incoming WebSocket messages.
   * Filter out pong messages from activity feed.
   */
  const handleMessage = useCallback((msg: WSMessage) => {
    // Skip pong messages in activity feed
    if (msg.type === 'pong') return
    
    // Add to activity feed
    const timestamp = msg.timestamp 
      ? new Date(msg.timestamp).toLocaleTimeString()
      : new Date().toLocaleTimeString()
    
    const newActivity = {
      id: `${Date.now()}-${Math.random()}`,
      type: msg.type,
      message: formatActivityMessage(msg),
      timestamp,
    }
    
    setActivity((prev) => [newActivity, ...prev.slice(0, 19)])
  }, [])
  
  /**
   * Register message handler on mount, unregister on unmount.
   */
  useEffect(() => {
    const unsubscribe = addMessageHandler(handleMessage)
    return unsubscribe
  }, [addMessageHandler, handleMessage])
  
  /**
   * Fetch initial stats and enabled modules.
   */
  useEffect(() => {
    async function fetchData() {
      // Fetch system status
      try {
        const status = await api.get<{
          status: string
          twitch_bot: string
          websocket_connections: number
          loaded_modules: number
          active_channels: number
          total_users: number
        }>('/api/admin/status')
        
        setSystemStatus(status)
        setStats((prev) => ({
          ...prev,
          activeChannels: status.active_channels,
        }))
      } catch (error) {
        console.error('Failed to fetch stats:', error)
        // Set a degraded status so UI still works
        setSystemStatus({
          status: 'error',
          twitch_bot: 'unknown',
          websocket_connections: 0,
        })
      }
      
      // Fetch available modules
      try {
        const modules = await api.get<EnabledModule[]>('/api/modules')
        setEnabledModules(modules)
      } catch (error) {
        console.error('Failed to fetch modules:', error)
      }
      
      // Fetch user's first channel for module widgets
      try {
        const channels = await api.get<Array<{ id: number }>>('/api/channels')
        if (channels.length > 0) {
          setSelectedChannelId(channels[0].id)
        }
      } catch (error) {
        console.error('Failed to fetch channels:', error)
      }
    }
    
    fetchData()
    
    // Refresh stats periodically
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [])
  
  // Suppress unused setSelectedChannelId warning - will be used when channel selector is added
  void setSelectedChannelId
  
  return (
    <div className="space-y-6">
      {/* ================================================================== */}
      {/* Top Row: System Status + Core Bot Stats                           */}
      {/* ================================================================== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SystemStatusCard 
          status={systemStatus} 
          isWsConnected={isConnected} 
        />
        <StatCard
          icon={Radio}
          label="Active Channels"
          value={stats.activeChannels}
          subValue="Channels with bot active"
          color="purple"
        />
        <StatCard
          icon={MessageSquare}
          label="Messages"
          value={stats.messagesToday}
          subValue="Received today"
          color="blue"
        />
        <StatCard
          icon={Zap}
          label="Commands"
          value={stats.commandsToday}
          subValue="Executed today"
          color="orange"
        />
      </div>
      
      {/* ================================================================== */}
      {/* Middle Row: Module Widgets                                        */}
      {/* ================================================================== */}
      <ModuleWidgetsRow
        enabledModules={enabledModules}
        isConnected={isConnected}
        selectedChannelId={selectedChannelId}
      />
      
      {/* ================================================================== */}
      {/* Bottom Row: Recent Activity (Full Width)                          */}
      {/* ================================================================== */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
          <span
            className={clsx(
              'flex items-center gap-1.5 text-xs px-2 py-1 rounded-full',
              isConnected ? 'bg-state-success-bg text-synth-mint' : 'bg-surface-overlay text-text-secondary'
            )}
          >
            {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
            {isConnected ? 'Live' : 'Offline'}
          </span>
        </div>
        
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {activity.length > 0 ? (
            activity.map((item) => (
              <ActivityItem key={item.id} {...item} />
            ))
          ) : (
            <p className="text-text-muted text-center py-8">
              No recent activity. Events will appear here in real-time.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
